import { ReactNode, createContext, useContext, useMemo, useCallback } from "react";

import { useWallets } from "@privy-io/react-auth/solana";
import { PublicKey, Transaction, VersionedTransaction } from "@solana/web3.js";
import { WALLET_ERROR, Result } from "@bridgesplit/utils";

import { buildLoopscaleWalletFromPrivy, getPrivyWalletMatch } from "./privyHelpers";
import { useAuthStorage } from "../reducers/authSlice";
import { getDebugWallet } from "../wallet/user";
import { UseAuthResponse, LoopscaleWallet } from "./types";

const LoopscaleWalletContext = createContext<UseAuthResponse | undefined>(undefined);

export function LoopscaleWalletProvider({ children }: { children: ReactNode }) {
    const contextValue = usePrivyWallet();

    const memoizedContextValue = useMemo(
        () => ({
            data: {
                icon: contextValue.data?.icon,
                userWalletAddress: contextValue.data?.userWalletAddress,
                userPublicKey: contextValue.data?.userPublicKey
            },
            isLoading: contextValue.isLoading,
            getWeb3UserResult: contextValue.getWeb3UserResult
        }),
        [
            contextValue.data?.userWalletAddress,
            contextValue.isLoading,
            contextValue.data?.userPublicKey,
            contextValue.data?.icon,
            contextValue.getWeb3UserResult
        ]
    );

    return <LoopscaleWalletContext.Provider value={memoizedContextValue}>{children}</LoopscaleWalletContext.Provider>;
}

export function useLoopscaleWalletContext(): UseAuthResponse {
    const context = useContext(LoopscaleWalletContext);

    if (!context) {
        throw new Error("useLoopscaleWalletContext must be used within a LoopscaleWalletProvider");
    }

    return context;
}

/**
 * Hook that provides Privy wallet functionality with the same API shape as useWallet() from @solana/wallet-adapter-react
 * This allows for easy swapping between Privy and the adapter.
 *
 * NOTE: For external wallets (Phantom, Backpack, etc.), this hook uses the first wallet
 * from privyWallets array, which automatically updates when you switch accounts in the
 * browser extension via the Wallet Standard.
 */

// if there is state you are connected. if there is state and they are not the same, its loading. if there is no state, no loading no connected

function createDebugWalletResponse(debugWallet: string | undefined): UseAuthResponse {
    const publicKey = debugWallet ? new PublicKey(debugWallet) : PublicKey.default;
    const loopscaleWallet: LoopscaleWallet = {
        publicKey,
        walletAddress: debugWallet || "",
        signTransaction: async <T extends Transaction | VersionedTransaction>(tx: T) =>
            Promise.reject(new Error("Debug wallet: signTransaction not implemented")),
        signAllTransactions: async <T extends Transaction | VersionedTransaction>(txs: T[]) =>
            Promise.reject(new Error("Debug wallet: signAllTransactions not implemented")),
        signMessage: undefined,
        walletClientType: undefined,
        signAndSendTransactions: async <T extends Transaction | VersionedTransaction>(txs: T[]) =>
            Promise.reject(new Error("Debug wallet: signAndSendTransactions not implemented"))
    };

    return {
        data: {
            icon: undefined,
            userWalletAddress: debugWallet ? debugWallet : undefined,
            userPublicKey: publicKey
        },
        isLoading: false,
        getWeb3UserResult: () => Result.ok(loopscaleWallet)
    };
}

function usePrivyWallet(): UseAuthResponse {
    const { wallets: privyWallets, ready } = useWallets();
    const { state } = useAuthStorage();
    const stateWalletAddress = state.stateWalletAddress;
    const stateClientType = state.walletClientType;
    const debugWallet = getDebugWallet();

    const privyWallet = useMemo(() => {
        if (!privyWallets.length || !stateWalletAddress) return undefined;
        return getPrivyWalletMatch(privyWallets, stateWalletAddress, stateClientType);
    }, [privyWallets, stateWalletAddress, stateClientType]);

    const isWalletSync = (!privyWallet && !stateWalletAddress) || privyWallet?.address === stateWalletAddress;

    const isLoading = !ready || (!!stateWalletAddress && !isWalletSync);

    const loopscaleWallet = useMemo<LoopscaleWallet | undefined>(() => {
        if (!stateWalletAddress || !privyWallet) return undefined;
        return buildLoopscaleWalletFromPrivy(privyWallet, stateClientType);
    }, [privyWallet, stateWalletAddress, stateClientType]);

    const getWeb3UserResult = useCallback((): Result<LoopscaleWallet> => {
        if (!loopscaleWallet) {
            return Result.err(WALLET_ERROR);
        }
        return Result.ok(loopscaleWallet);
    }, [loopscaleWallet]);

    // Debug wallet response - only return after Redux is synced (stateWalletAddress === debugWallet)
    const debugResponse = useMemo(() => {
        if (!debugWallet || stateWalletAddress !== debugWallet) return undefined;
        return createDebugWalletResponse(debugWallet);
    }, [stateWalletAddress, debugWallet]);

    // Return debug response when Redux has been synced with debugWallet
    if (debugResponse) {
        return debugResponse;
    }

    // Loading state - waiting for wallet to be set in Redux
    if (isLoading || !isWalletSync) {
        return {
            data: {
                icon: undefined,
                userWalletAddress: undefined,
                userPublicKey: undefined
            },
            isLoading,
            getWeb3UserResult
        };
    }

    return {
        data: {
            icon: privyWallet?.standardWallet?.icon || undefined,
            userWalletAddress: stateWalletAddress || undefined,
            userPublicKey: stateWalletAddress ? new PublicKey(stateWalletAddress) : undefined
        },
        isLoading: false,
        getWeb3UserResult
    };
}
